import React, { useRef, useMemo, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { History, Upload, Trash2, Lock, Eye, Download } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// ─── Types ──────────────────────────────────────────────────────────────────

interface EmpDocumento {
  id: string;
  contrato_id: string;
  titulo: string;
  tipo_documental: string;
  familia: string;
  origem: string;
  codigo_documento: string | null;
  descricao: string | null;
  ativo: boolean;
  created_at: string;
}

interface VirtualDocumentTableProps {
  docs: EmpDocumento[];
  latestVersions: Record<string, number>;
  tipoLabel: (tipo: string) => string;
  canEdit: boolean;
  lockedDocIds?: Set<string>;
  onViewDocument: (docId: string) => void;
  onDownloadDocument: (docId: string) => void;
  onViewVersions: (docId: string) => void;
  onUploadVersion: (docId: string) => void;
  onArchive: (docId: string) => void;
  /** When > 0, show pagination controls instead of all rows */
  pageSize?: number;
}

const ROW_HEIGHT = 52;
const VIRTUAL_THRESHOLD = 200; // use virtual scrolling above this count

const fmtDate = (d: string | null) => {
  if (!d) return '—';
  try { return format(new Date(d), 'dd/MM/yyyy', { locale: ptBR }); } catch { return d; }
};

// ─── Component ──────────────────────────────────────────────────────────────

export const VirtualDocumentTable: React.FC<VirtualDocumentTableProps> = ({
  docs,
  latestVersions,
  tipoLabel,
  canEdit,
  lockedDocIds = new Set(),
  onViewDocument,
  onDownloadDocument,
  onViewVersions,
  onUploadVersion,
  onArchive,
  pageSize = 50,
}) => {
  const [page, setPage] = useState(0);
  const parentRef = useRef<HTMLDivElement>(null);
  const useVirtual = docs.length >= VIRTUAL_THRESHOLD;

  // Pagination
  const totalPages = Math.ceil(docs.length / pageSize);
  const visibleDocs = useMemo(() => {
    if (useVirtual) return docs; // virtual scrolling handles its own windowing
    return docs.slice(page * pageSize, (page + 1) * pageSize);
  }, [docs, page, pageSize, useVirtual]);

  // Virtual scrolling
  const virtualizer = useVirtualizer({
    count: docs.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 20,
    enabled: useVirtual,
  });

  const renderRow = (doc: EmpDocumento) => {
    const isLocked = lockedDocIds.has(doc.id);
    const hasVersion = (latestVersions[doc.id] || 0) > 0;
    return (
      <tr
        key={doc.id}
        className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted"
      >
        <td className="p-2 align-middle">
          <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">
            {doc.codigo_documento || '—'}
          </code>
        </td>
        <td className="p-2 align-middle">
          <div className="flex items-center gap-1.5">
            {isLocked && <Lock className="h-3.5 w-3.5 text-warning flex-shrink-0" />}
            <div className="min-w-0">
              <p className="font-medium text-sm truncate">{doc.titulo}</p>
              {doc.descricao && (
                <p className="text-xs text-muted-foreground truncate max-w-[250px]">{doc.descricao}</p>
              )}
            </div>
          </div>
        </td>
        <td className="p-2 align-middle">
          <Badge variant="outline" className="text-xs">{tipoLabel(doc.tipo_documental)}</Badge>
        </td>
        <td className="p-2 align-middle text-center">
          {hasVersion ? (
            <Badge variant="secondary" className="text-xs">v{latestVersions[doc.id]}</Badge>
          ) : (
            <Badge variant="outline" className="text-xs text-muted-foreground">—</Badge>
          )}
        </td>
        <td className="p-2 align-middle">
          <Badge variant={doc.origem === 'manual' ? 'outline' : 'secondary'} className="text-xs">
            {doc.origem === 'manual' ? 'Manual' : 'Auto'}
          </Badge>
        </td>
        <td className="p-2 align-middle text-xs text-muted-foreground">
          {fmtDate(doc.created_at)}
        </td>
        <td className="p-2 align-middle">
          <div className="flex items-center justify-end gap-1">
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" disabled={!hasVersion} onClick={() => onViewDocument(doc.id)}>
                    <Eye className={`h-4 w-4 ${!hasVersion ? 'opacity-30' : ''}`} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{hasVersion ? 'Visualizar documento' : 'Sem arquivo disponível'}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" disabled={!hasVersion} onClick={() => onDownloadDocument(doc.id)}>
                    <Download className={`h-4 w-4 ${!hasVersion ? 'opacity-30' : ''}`} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{hasVersion ? 'Baixar documento' : 'Sem arquivo disponível'}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={() => onViewVersions(doc.id)}>
                    <History className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Histórico de versões</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {canEdit && !isLocked && (
              <>
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" onClick={() => onUploadVersion(doc.id)}>
                        <Upload className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Nova versão</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" onClick={() => onArchive(doc.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Arquivar</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
            {isLocked && (
              <span className="text-xs text-warning ml-1">Em uso</span>
            )}
          </div>
        </td>
      </tr>
    );
  };

  // ── Header ──────────────────────────────────────────────────────────────

  const header = (
    <thead className="[&_tr]:border-b">
      <tr className="border-b transition-colors hover:bg-muted/50">
        <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground text-xs">Código</th>
        <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground text-xs">Título</th>
        <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground text-xs">Tipo</th>
        <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-xs">Versão</th>
        <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground text-xs">Origem</th>
        <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground text-xs">Data</th>
        <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground text-xs">Ações</th>
      </tr>
    </thead>
  );

  // ── Virtual rendering ───────────────────────────────────────────────────

  if (useVirtual) {
    return (
      <div className="w-full">
        <table className="w-full caption-bottom text-sm">
          {header}
        </table>
        <div
          ref={parentRef}
          className="overflow-auto"
          style={{ maxHeight: Math.min(docs.length * ROW_HEIGHT, 600) }}
        >
          <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
            <table className="w-full caption-bottom text-sm">
              <tbody>
                {virtualizer.getVirtualItems().map(virtualRow => {
                  const doc = docs[virtualRow.index];
                  return (
                    <tr
                      key={doc.id}
                      className="border-b transition-colors hover:bg-muted/50"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      <td className="p-2 align-middle">
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">
                          {doc.codigo_documento || '—'}
                        </code>
                      </td>
                      <td className="p-2 align-middle">
                        <div className="flex items-center gap-1.5">
                          {lockedDocIds.has(doc.id) && <Lock className="h-3.5 w-3.5 text-warning flex-shrink-0" />}
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{doc.titulo}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-2 align-middle">
                        <Badge variant="outline" className="text-xs">{tipoLabel(doc.tipo_documental)}</Badge>
                      </td>
                      <td className="p-2 align-middle text-center">
                        {(latestVersions[doc.id] || 0) > 0 ? (
                          <Badge variant="secondary" className="text-xs">v{latestVersions[doc.id]}</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-muted-foreground">—</Badge>
                        )}
                      </td>
                      <td className="p-2 align-middle">
                        <Badge variant={doc.origem === 'manual' ? 'outline' : 'secondary'} className="text-xs">
                          {doc.origem === 'manual' ? 'Manual' : 'Auto'}
                        </Badge>
                      </td>
                      <td className="p-2 align-middle text-xs text-muted-foreground">
                        {fmtDate(doc.created_at)}
                      </td>
                      <td className="p-2 align-middle">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" title="Visualizar" onClick={() => onViewDocument(doc.id)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" title="Baixar" onClick={() => onDownloadDocument(doc.id)}>
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" title="Versões" onClick={() => onViewVersions(doc.id)}>
                            <History className="h-4 w-4" />
                          </Button>
                          {canEdit && !lockedDocIds.has(doc.id) && (
                            <>
                              <Button variant="ghost" size="icon" title="Nova versão" onClick={() => onUploadVersion(doc.id)}>
                                <Upload className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" title="Arquivar" onClick={() => onArchive(doc.id)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                          {lockedDocIds.has(doc.id) && (
                            <span className="text-xs text-warning ml-1">Em uso</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-3 py-2 text-xs text-muted-foreground border-t">
          {docs.length.toLocaleString()} documentos (virtual scrolling ativo)
        </div>
      </div>
    );
  }

  // ── Paginated rendering ─────────────────────────────────────────────────

  return (
    <div className="w-full">
      <table className="w-full caption-bottom text-sm">
        {header}
        <tbody className="[&_tr:last-child]:border-0">
          {visibleDocs.map(doc => renderRow(doc))}
        </tbody>
      </table>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-3 py-2 border-t text-xs text-muted-foreground">
          <span>
            {page * pageSize + 1}–{Math.min((page + 1) * pageSize, docs.length)} de {docs.length.toLocaleString()}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              disabled={page === 0}
              onClick={() => setPage(p => p - 1)}
            >
              Anterior
            </Button>
            <span className="px-2">{page + 1}/{totalPages}</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              disabled={page >= totalPages - 1}
              onClick={() => setPage(p => p + 1)}
            >
              Próximo
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
